import { MarketingNav } from '@/components/layout/marketing-nav'
import { SiteFooter } from '@/components/layout/site-footer'
import { Link } from 'react-router-dom'

export function TermsOfServicePage() {
  return (
    <div className="flex flex-col min-h-screen bg-white">
      <MarketingNav variant="standalone" />

      <div className="container max-w-4xl py-12 px-4 pt-32">
        {/* Header */}
        <div className="mb-12 scroll-fade-up in-view">
          <h1 className="text-4xl font-medium text-slate-900 mb-4">Pelgo Terms of Use</h1>
          <p className="text-sm text-slate-600">
            <strong>Last Updated</strong> February 24, 2026
          </p>
        </div>

        {/* Content */}
        <div className="prose prose-slate max-w-none space-y-10 scroll-fade-up">
          {/* Welcome */}
          <section id="welcome">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Welcome</h2>
            <p className="text-slate-700 leading-relaxed">
              Welcome to Pelgo! Pelgo, Inc. (&quot;we,&quot; &quot;us,&quot; &quot;ours,&quot; or
              &quot;Pelgo&quot;) provides the platform by combining AI-driven job services with a
              centralized command center for people to track placement success.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              You are a user (&quot;you,&quot; &quot;your,&quot; &quot;yours,&quot; or
              &quot;User&quot;) granted access to the electronic systems, applications, and internet
              sites of Pelgo, including{' '}
              <a
                href="https://www.pelgo.com/#how-it-works"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                https://www.pelgo.com/#how-it-works
              </a>{' '}
              and others, and through which we offer and provide the services (the
              &quot;Platform&quot;).
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              Please review these Pelgo Terms of Use (&quot;User Terms&quot;) carefully. By using
              the Platform, you agree to all of the terms and conditions in these User Terms and
              they become a binding legal commitment between you and Pelgo. Sometimes in these User
              Terms we call you and us a &quot;party&quot; or together, the &quot;parties.&quot;
              There are other defined terms throughout these User Terms. You can tell if a term is
              being defined if it has quotation marks around it. Defined terms are capitalized when
              used again in the User Terms, except for when we refer to you/your/yours, us/we/ours,
              or party/parties.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4 font-semibold">
              IN THESE USER TERMS, YOU WAIVE YOUR RIGHTS TO PARTICIPATE IN A JURY TRIAL AS PROVIDED
              IN SECTION 7.
            </p>
            <ul className="list-disc list-inside space-y-2 pl-4 text-slate-700">
              <li>Web-based and SMS communication channels</li>
              <li>AI-driven career guidance and support</li>
              <li>Onboarding assistance and verification services</li>
              <li>Career transition resources and reminders</li>
              <li>Personalized support notifications</li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              We reserve the right to update or modify these User Terms at any time without prior
              notice. We encourage you to visit this page regularly for any changes.
            </p>
          </section>

          {/* The Platform and the Pelgo Property */}
          <section id="platform-property">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              The Platform and the Pelgo Property
            </h2>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">
              Our Ownership of the Platform and the Pelgo Property
            </h3>
            <p className="text-slate-700 leading-relaxed">
              We own all of the following, which we sometimes collectively call the &quot;Pelgo
              Property&quot;:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li>The Platform;</li>
              <li>
                The &quot;Pelgo Content,&quot; which means content or other information created,
                derived, owned, or licensed by Pelgo that we make available in connection with the
                services or on the Platform; and
              </li>
              <li>
                &quot;Usage Data,&quot; which means any technical data or other information
                collected, created, derived, or aggregated by us about the use or performance of the
                Platform, excluding User Content (defined in Section 3 below).
              </li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              Between you and us, we own all right, title, and interest to the Pelgo Property, all
              components and derivative works of the Pelgo Property, all improvements to the Pelgo
              Property, all Feedback (defined in Section 3 below), and the copyrights, patents,
              trade secrets, trademarks, and other intellectual property rights pertaining to any
              aspect of the Pelgo Property. We reserve all rights in the Pelgo Property not
              expressly granted to you in these User Terms. You acquire no ownership interest,
              derivative work, or component of the Pelgo Property through your use of it. You are
              not granted right, title, or interest to use any trademark, service mark, logo, or
              trade name of Pelgo.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">
              Errors, Inaccuracies, and Omissions
            </h3>
            <p className="text-slate-700 leading-relaxed">
              The Platform or the Pelgo Content may contain typographical, technical, photographic,
              or other errors, inaccuracies, or omissions. We do not guarantee that the Pelgo
              Content or any other information contained on the Platform is accurate, complete, or
              current. We have the right, but not the obligation, to correct any errors,
              inaccuracies, or omissions.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Changes</h3>
            <p className="text-slate-700 leading-relaxed">
              You acknowledge that the Platform, or the Pelgo Content, including any feature, may be
              updated, removed, or otherwise be subject to change in our sole discretion and without
              notice to you. You agree that we will not be liable to you or any third party for any
              change, suspension, or discontinuance of the Platform, the Pelgo Content, or any
              feature of them.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">
              Third-Party Links and Services
            </h3>
            <p className="text-slate-700 leading-relaxed">
              The Platform may include links, integrations, or connections to third-party websites,
              applications, services, or content (including User Content). This inclusion does not
              imply review or endorsement by us; you proceed at your own risk when you use a
              third-party website, application, service, or content. We do not warrant, and are not
              responsible for, the services, products, statements, or claims made by or about a
              third party, or the actions or omissions of any third party. You must review and
              comply with any third-party terms of service or other provisions.
            </p>
          </section>

          {/* Your Use of the Platform */}
          <section id="your-use">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Your Use of the Platform</h2>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Your Permitted Use</h3>
            <p className="text-slate-700 leading-relaxed">
              You must be 16 years of age or older to access or use the Platform. As a parent or
              guardian, you may provide or make available personal information of your children to
              us via the Platform, and, by doing so, you agree and consent to our collection and use
              of your children&apos;s information.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              The Platform may only be used for the internal business purposes of the User. You
              represent that you are 16 years of age or older and you are authorized to use the
              Platform.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              You may access the Platform via platform access, which provides a user with access to
              a User&apos;s account. You must comply with all of the terms and conditions contained
              in these User Terms while you access or use the Platform or other Pelgo Property.
              Please contact us if you at any time or for any reason want to terminate your access
              to the Platform, including if you do not agree with these User Terms or any update or
              change to these User Terms.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">
              Feedback and Comments
            </h3>
            <p className="text-slate-700 leading-relaxed">
              We encourage you to submit Feedback to us. &quot;Feedback&quot; means any suggestion,
              enhancement request, recommendation, correction, change, or other feedback regarding
              the Platform, or other Pelgo Property. We also collect Usage Data about the use and
              performance of the Platform. We own all Feedback, all Usage Data, as well as all
              improvements, modifications, and changes to the Pelgo Property based on such Feedback
              or Usage Data. You assign us all right, title, and interest to any Feedback provided
              to us and Usage Data collected by us. To the extent that we require further rights,
              you grant us an exclusive, royalty-free, perpetual, irrevocable, transferable, and
              sublicensable worldwide license to access, store, process, use, copy, modify, remove,
              translate, display, perform, publish, disclose, distribute, transfer, sublicense, and
              export Feedback provided by and Usage Data collected from you, in any form or medium,
              without restriction. We have no obligation to pay any compensation for Feedback or
              Usage Data. You may not repost, republish, or redistribute Feedback or Usage Data. We
              have the right, but do not assume any responsibility to review, screen, or approve
              Feedback. You are responsible for the accuracy and legality of any Feedback you make.
              We take no responsibility and assume no liability for any Feedback posted or
              transmitted by you, other users, or third parties.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">
              Content You Submit via the Platform
            </h3>
            <p className="text-slate-700 leading-relaxed">
              When you submit content or information (such as data, text, audio, video, or images)
              in connection with the use of the Platform at any time (excluding Usage Data and
              Feedback), such content or information is controlled by you (&quot;User
              Content&quot;). We are not responsible or liable for the User Content, including the
              accuracy, quality, integrity, legality, reliability, and appropriateness of the User
              Content.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              You have authorized us to access, store, process, use, copy, record, and modify User
              Content for the purposes of providing the Platform, maintaining or improving the
              Platform, or as otherwise permitted by our agreement with or in writing by the User.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              We may delete any and all User Content at your direction or at the conclusion of the
              services. We have no duty to store or backup the User Content.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Privacy Policy</h3>
            <p className="text-slate-700 leading-relaxed">
              Please review our{' '}
              <Link to="/privacy" className="text-blue-600 hover:underline">
                Privacy Policy
              </Link>{' '}
              for more information on how we collect and use personal information.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Prohibited Uses</h3>
            <p className="text-slate-700 leading-relaxed">
              You shall not do any of the following in connection with the Platform:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li>
                Decompile, disassemble, or reverse engineer the Platform, or attempt to obtain or
                perceive the source code from which any component of the Platform is compiled or
                interpreted
              </li>
              <li>
                Duplicate or create any derivative product from the Pelgo Property or any component
                of the Pelgo Property
              </li>
              <li>
                License, sublicense, lease, resell, or transfer any of the Pelgo Property to a third
                party or allow third parties to gain access to the Pelgo Property
              </li>
              <li>
                Access the Pelgo Property if you are a direct competitor of us, access the Pelgo
                Property for a competitive purpose, or provide any Pelgo Property, directly or
                indirectly, to any competitor of ours (including any screenshots of the Platform or
                reports regarding the services)
              </li>
              <li>
                Remove any copyright, trademark, or other proprietary notions from the Pelgo
                Property
              </li>
              <li>
                Transmit material containing viruses, malware, or other harmful or deleterious
                computer code, files, scripts, agents, or programs through the Platform
              </li>
              <li>Interfere with or disrupt the integrity or performance of the Platform</li>
              <li>
                Attempt to bypass, exploit, defeat, or disable limitations or restrictions placed on
                the Platform
              </li>
              <li>
                Conduct any denial of service (DoS) attack on the Platform or otherwise attempt to
                disrupt, disable, or overload the Platform
              </li>
              <li>Attempt to gain access to the Platform by automated means, such as bots</li>
              <li>
                Attempt to gain unauthorized access to the Platform, or the computer systems or
                networks related to the Platform
              </li>
              <li>
                Create a false identity or attempt to mislead others as to your identity or the
                identity of the sender or the origin of any data or communications
              </li>
              <li>Use the Platform to violate any law or rights of others</li>
              <li>Interfere with another person&apos;s use and enjoyment of the Platform</li>
              <li>
                Use or export the Pelgo Property in violation of US export laws and regulations
              </li>
            </ul>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">
              Suspension or Termination of Your Access and Removal of Content
            </h3>
            <p className="text-slate-700 leading-relaxed">
              We may suspend or terminate your access to the Platform or the User&apos;s account or
              remove any content you submit immediately and indefinitely if we, in good faith,
              believe: that you have materially breached any provision of these User Terms; that you
              are using the Platform in a manner that threatens the security, integrity, or
              reliability of the services; that there has been unauthorized access or fraud related
              to your access to the User&apos;s account or the services; or that the provision of
              the services (or a portion thereof) will expose us or you to legal, regulatory, or
              compliance risk.
            </p>
          </section>

          {/* Your Access to the Platform */}
          <section id="your-access">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">
              Your Access to the Platform
            </h2>
            <p className="text-slate-700 leading-relaxed">
              You may create or obtain access credentials (for example, username and password) to
              the User account or to access certain features of the Platform. You must have unique
              access credentials. You may not share access credentials with another user or any
              third party. You are responsible for maintaining the confidentiality of your access
              credentials. You agree to use prudent and reasonable efforts to prevent unauthorized
              access to or use of the Platform or the User&apos;s account. You will notify us
              immediately of any unauthorized access to or use of your access credentials, the
              Platform, or the User&apos;s account. We have no liability to you for any unauthorized
              access to or use of your access credentials, the Platform, or the User&apos;s account
              caused by your acts, omissions, or breach of these User Terms.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              You agree that the Platform is a tool for retrieving, analyzing, and managing User
              Content and is not a storage system or other system of record for User Content. You
              are responsible for extracting and backing up User Content onto your networks and
              systems on a regular basis and taking appropriate steps to safeguard and ensure the
              integrity of your User Content. We will not be responsible for any backup, recovery,
              or other steps to ensure User Content is recoverable in the event of a data loss. We
              expressly disclaim any obligations with respect to storage of User Content.
            </p>
          </section>

          {/* Social Media */}
          <section id="social-media">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Social Media</h2>
            <p className="text-slate-700 leading-relaxed">
              As part of the functionality of the Platform, you may link your account with online
              accounts you have with third-party service providers (each such account, a
              &quot;Third-Party Account&quot;) by either: (1) providing your Third-Party Account
              login information through the Platform; or (2) allowing us to access your Third-Party
              Account, as is permitted under the applicable terms and conditions that govern your
              use of each Third-Party Account. You represent and warrant that you are entitled to
              disclose your Third-Party Account login information to us and/or grant us access to
              your Third-Party Account, without breach by you of any of the terms and conditions
              that govern your use of the applicable Third-Party Account, and without obligating us
              to pay any fees or making us subject to any usage limitations imposed by the
              third-party service provider of the Third-Party Account.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              By granting us access to any Third-Party Accounts, you understand that (1) we may
              access, make available, and store (if applicable) any content that you have provided
              to and stored in your Third-Party Account (the &quot;Social Network Content&quot;) so
              that it is available on and through the Platform via your account, including without
              limitation any friend lists and (2) we may submit to and receive from your Third-Party
              Account additional information to the extent you are notified when you link your
              account with the Third-Party Account.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              Depending on the Third-Party Accounts you choose and subject to the privacy settings
              that you have set in such Third-Party Accounts, personal information that you post to
              your Third-Party Accounts may be available on and through your account on the
              Platform. Please note that if a Third-Party Account or associated service becomes
              unavailable or our access to such Third-Party Account is terminated by the third-party
              service provider, then Social Network Content may no longer be available on and
              through the Platform. You will have the ability to disable the connection between your
              account on the Platform and your Third-Party Accounts at any time.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4 font-semibold">
              PLEASE NOTE THAT YOUR RELATIONSHIP WITH THE THIRD-PARTY SERVICE PROVIDERS ASSOCIATED
              WITH YOUR THIRD-PARTY ACCOUNTS IS GOVERNED SOLELY BY YOUR AGREEMENT(S) WITH SUCH
              THIRD-PARTY SERVICE PROVIDERS.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              We make no effort to review any Social Network Content for any purpose, including but
              not limited to, for accuracy, legality, or non-infringement, and we are not
              responsible for any Social Network Content. You acknowledge and agree that we may
              access your email address book associated with a Third-Party Account and your contacts
              list stored on your mobile device or tablet computer solely for purposes of
              identifying and informing you of those contacts who have also registered to use the
              Platform. You can deactivate the connection between the Platform and your Third-Party
              Account by contacting us using the contact information below or through your account
              settings (if applicable). We will attempt to delete any information stored on our
              servers that was obtained through such Third-Party Account, except the username and
              profile picture that become associated with your account.
            </p>
          </section>

          {/* User Subscriptions */}
          <section id="subscriptions">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">User Subscriptions</h2>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Cancellation</h3>
            <p className="text-slate-700 leading-relaxed">
              All purchases are non-refundable. You can cancel your subscription at any time by
              contacting us using the contact information provided below. Your cancellation will
              take effect at the end of the current paid term. If you have any questions or are
              unsatisfied with our services, please email us at{' '}
              <a href="mailto:legal@pelgo.com" className="text-blue-600 hover:underline">
                legal@pelgo.com
              </a>
              .
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Fee Changes</h3>
            <p className="text-slate-700 leading-relaxed">
              We may, from time to time, make changes to the subscription fee and will communicate
              any price changes to you in accordance with applicable law.
            </p>
          </section>

          {/* No Warranties */}
          <section id="no-warranties">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">No Warranties</h2>
            <p className="text-slate-700 leading-relaxed">
              ALL SALES ARE FINAL, AND PELGO DOES NOT OFFER ANY MONEY-BACK GUARANTEES. YOU RECOGNIZE
              AND AGREE THAT YOU SHALL NOT BE ENTITLED TO A REFUND UNDER ANY CIRCUMSTANCES AND ANY
              REFUNDS OR CREDITS OFFERED ARE WITHIN PELGO&apos;S SOLE DISCRETION. YOU ARE
              RESPONSIBLE FOR AND ASSUME ALL RISKS IN CONNECTION WITH YOUR USE OF THE PLATFORM.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              THE PLATFORM, THE PELGO PROPERTY, AND ANY OTHER CONTENT, DATA, SOFTWARE, PRODUCTS, AND
              PLATFORM PROVIDED IN CONNECTION WITH THE PLATFORM ARE PROVIDED &quot;AS IS,&quot;
              &quot;WITH ALL FAULTS,&quot; AND &quot;AS AVAILABLE.&quot; WE MAKE NO REPRESENTATIONS
              OR WARRANTIES TO YOU OF ANY KIND IN CONNECTION WITH THE PLATFORM, AND, TO THE FULLEST
              EXTENT PERMITTED BY APPLICABLE LAW, WE EXPRESSLY DISCLAIM ALL WARRANTIES OF ANY KIND
              OR NATURE, WHETHER EXPRESS, IMPLIED, OR STATUTORY.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              We expressly disclaim any implied warranties of merchantability, fitness for a
              particular purpose, title, or non-infringement. We expressly disclaim any warranties
              of system integration, non-interference, absence of any defects (whether latent or
              patent), or security of data. We do not warrant or make any representations concerning
              the accuracy, completeness, quality, usability, or reliability of information or
              materials provided in connection with the Platform, found on our Platform, or linked
              to our Platform. We expressly disclaim any warranty related to third-party websites or
              other third-party content that may be accessed through our Platform. The Platform may
              be subject to limitations, delays, and other problems inherent in the use of the
              internet and electronic communications. We are not responsible for any delays,
              delivery failures, or other damages resulting from such problems.
            </p>
          </section>

          {/* Limitations on Liability */}
          <section id="limitations">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Limitations on Liability</h2>
            <p className="text-slate-700 leading-relaxed">
              All of the limitations on liability in this section apply to the fullest extent
              permitted by applicable law: (A) regardless of the theory of liability or form of
              action; (B) regardless of the cause of the damages, including negligence; (C) even if
              we have been advised of the possibility of such damages; (D) without regard to whether
              other provisions of these User Terms have been breached or any remedies have failed
              their essential purpose; and (E) to any claims you may bring against any third party
              to the extent that we would be required to indemnify that third party for such claim.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              We have no liability for certain types of damages. You agree to limit claims for
              damages (or other monetary relief) against us to direct and actual damages. You will
              not seek any indirect, incidental, special, consequential, treble, or punitive damages
              from us. We have no liability for damages related to: unauthorized access to, loss, or
              theft of your information or data, lost profits, costs of delay, failure of delivery,
              business interruption, costs of procurement of substitute goods, services, or
              technology, or personal injury or death. We have no liabilities to third parties
              arising from any source.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              We are not liable for damages out of our control caused by you, another user, or a
              third party, by any act of nature, by any act beyond our reasonable control (for
              example, war, terrorist acts, labor disputes, government actions, pandemics), or by
              any criminal activity by someone unrelated to us.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              To the fullest extent permitted by law, the total aggregate liability of Pelgo, our
              affiliates, and our third-party providers, licensors, or suppliers to you arising or
              relating to the Platform, these User Terms or our Privacy Policy is $100.
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              You and we each agree that all claims related to the Platform, these User Terms, or
              our Privacy Policy must be brought within two (2) years of the date the claim arises
              (even if applicable law provides for a longer statute of limitations).
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              The limitations on liability in this section constitute an important part of these
              User Terms and are among the reasons you and we are willing to enter into the
              agreements in these User Terms.
            </p>
          </section>

          {/* Disputes and Claims */}
          <section id="disputes">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Disputes and Claims</h2>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Claims Against Us</h3>
            <h4 className="text-lg font-semibold text-slate-900 mt-4 mb-2">
              Step 1: User Dispute Process
            </h4>
            <p className="text-slate-700 leading-relaxed">
              If you have a dispute with or claim against us, you must give us an opportunity to
              resolve your claim informally by sending a written description of your claim to{' '}
              <a href="mailto:legal@pelgo.com" className="text-blue-600 hover:underline">
                legal@pelgo.com
              </a>
              . You and we each agree to negotiate your claim in good faith. You agree that you may
              not commence any court proceeding unless you and we are unable to resolve the claim
              within 60 days after we receive your claim description and you have made a good faith
              effort to resolve your claim directly with us during that time.
            </p>
            <h4 className="text-lg font-semibold text-slate-900 mt-4 mb-2">
              Court Proceedings; Jury Trial Waiver
            </h4>
            <p className="text-slate-700 leading-relaxed">
              Court proceedings must be brought in the state or federal courts located in Delaware
              or the state or jurisdiction in which you reside, but not outside of the United
              States. YOU AND WE EACH WAIVE ANY RIGHT TO A JURY TRIAL.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Injunctive Relief</h3>
            <p className="text-slate-700 leading-relaxed">
              You and we acknowledge and agree that a breach by you of your responsibilities under
              Sections 3 or 4 of these User Terms could cause us irreparable harm for which monetary
              damages would not be an adequate remedy. You and we agree that, in the event of such
              breach or threatened breach, we will be entitled to equitable relief, including,
              without limitation, a restraining order, an injunction, specific performance, and any
              other relief that may be available from any court, without any requirement to post a
              bond or other security, or to prove actual damages or that monetary damages are not an
              adequate remedy. These remedies are not exclusive and are in addition to all other
              remedies that may be available at law, in equity, or otherwise.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">
              Notice and Procedure for Making Claims of Copyright Infringement
            </h3>
            <p className="text-slate-700 leading-relaxed">
              The Digital Millennium Copyright Act of 1998 (the &quot;DMCA&quot;) provides recourse
              for copyright owners who believe that material appearing on the Internet infringes
              upon their rights under US copyright law. If you believe that materials we host on or
              through the Platform or the Pelgo Content infringe upon your copyright rights, you may
              request that the material be removed or that access to it be blocked. If you believe
              that a notice of copyright infringement has been wrongly filed against you, the DMCA
              permits you to send us a counter-notice. Your notice or counter-notice to us must
              include:
            </p>
            <ul className="list-disc list-inside space-y-2 text-slate-700 ml-4 mt-4">
              <li>
                An electronic or physical signature of the person authorized to act on behalf of the
                owner of the copyright interest
              </li>
              <li>A description of the copyrighted work that you claim has been infringed upon</li>
              <li>
                A description of where the material that you claim is infringing is located on the
                Platform
              </li>
              <li>Your address, telephone number, and email address</li>
              <li>
                A statement by you that you have a good faith belief that the disputed use is not
                authorized by the copyright owner, its agent, or the law
              </li>
              <li>
                A statement by you, made under penalty of perjury, that the above information in
                your notice is accurate and that you are the copyright owner or authorized to act on
                the copyright owner&apos;s behalf
              </li>
            </ul>
            <p className="text-slate-700 leading-relaxed mt-4">
              Notices and counter-notices must meet the statutory requirements imposed by the DMCA.
              We suggest that you consult your legal advisor before submitting a notice or
              counter-notice. Please be aware that there can be substantial penalties for false
              claims. DMCA notices and counter-notices for the Platform should be sent to{' '}
              <a href="mailto:legal@pelgo.com" className="text-blue-600 hover:underline">
                legal@pelgo.com
              </a>
              .
            </p>
          </section>

          {/* General */}
          <section id="general">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">General</h2>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Choice of Law</h3>
            <p className="text-slate-700 leading-relaxed">
              These User Terms are governed by the laws of the state of Delaware, without regard to
              the conflicts of laws rules.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Notices</h3>
            <p className="text-slate-700 leading-relaxed">
              You may deliver notices to us by email to{' '}
              <a href="mailto:legal@pelgo.com" className="text-blue-600 hover:underline">
                legal@pelgo.com
              </a>
              . If you are commencing a legal proceeding against us, we do not accept notice or
              service via email and you must serve us with legal process as required by applicable
              law. We may deliver notices to you via email, mail, or electronic means using your
              contact information in the User&apos;s account or by posting the notice on the
              Platform. Electronic notices are considered delivered when sent or posted. Postal
              notices are considered delivered 3 days after mailing. Notices delivered by a
              nationally recognized courier (for example, UPS and FedEx) are considered delivered
              when received.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">
              Electronic Communications and Signatures (E-Sign) Consent
            </h3>
            <p className="text-slate-700 leading-relaxed">
              We may need to provide you with certain communications, notices, agreements, or
              disclosures (&quot;Communications&quot;) in writing regarding the Platform. You agree
              and consent to receive Communications electronically from us, our affiliates, and our
              third-party service providers, rather than in paper form, and to the use of electronic
              signatures in our relationship with you. You may withdraw your consent to receive
              Communications electronically at any time by sending us notice at the email address
              above. If you choose to withdraw your consent, we may terminate your access to the
              Platform.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">
              Third-Party Beneficiaries
            </h3>
            <p className="text-slate-700 leading-relaxed">
              Our third-party providers, licensors, and suppliers and our affiliates are considered
              to be third-party beneficiaries of these User Terms solely to the extent necessary for
              them to enforce any protections afforded them by these User Terms. There are no other
              third-party beneficiaries to these User Terms.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Assignment</h3>
            <p className="text-slate-700 leading-relaxed">
              You are prohibited from assigning these User Terms to a third party without the prior
              written consent of us. We may assign these User Terms and/or our rights and
              obligations under these User Terms, in whole or in part, without notice to you or your
              consent: (i) in connection with a merger, acquisition, corporate reorganization or
              sale of substantially all our assets, or (ii) to an affiliate of ours. Subject to the
              foregoing restrictions on assignment, these User Terms will bind and inure to the
              benefit of your and our respective successors and permitted assigns.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Waiver</h3>
            <p className="text-slate-700 leading-relaxed">
              You and we cannot waive any provision of these User Terms except in a writing signed
              by you and us. Our failure to insist on or enforce strict performance of any provision
              of these User Terms or any of our rights is not a waiver of any provision or right.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Severability</h3>
            <p className="text-slate-700 leading-relaxed">
              If any part of these User Terms is held invalid or unenforceable, that part may be
              severed from these User Terms to the minimum extent necessary to cure such invalidity
              or unenforceability.
            </p>
            <h3 className="text-xl font-semibold text-slate-900 mt-6 mb-3">Amendment</h3>
            <p className="text-slate-700 leading-relaxed">
              We reserve the right to amend these User Terms at our discretion and at any time. When
              we do, we will post the revised User Terms on the Platform with a new &quot;Last
              Updated&quot; date. We may, but are not required to, also provide you with notice of
              the amended User Terms via any other means we consider reasonable. Your continued use
              of the Platform following the posting of changes constitutes your acceptance of such
              changes. We encourage you to visit this page regularly for any changes.
            </p>
            <div className="mt-4 p-6 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-slate-700">
                <strong>Pelgo Inc.</strong>
              </p>
              <p className="text-slate-700">838 Walker Road, Suite 21-2</p>
              <p className="text-slate-700">Dover, Delaware 19904</p>
              <p className="text-slate-700 mt-3">Email: legal@pelgo.com</p>
              <p className="text-slate-700">Support: support@pelgo.com</p>
            </div>
          </section>

          {/* Contact */}
          <section id="contact">
            <h2 className="text-2xl font-semibold text-slate-900 mb-4">Contact Us</h2>
            <p className="text-slate-700 leading-relaxed">
              For questions about these User Terms or the Platform, please contact us at:
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              <strong>Pelgo, Inc.</strong>
              <br />
              838 Walker Rd, Suite 21-2
              <br />
              Dover, DE 19904
              <br />
              United States
            </p>
            <p className="text-slate-700 leading-relaxed mt-4">
              <a href="mailto:legal@pelgo.com" className="text-blue-600 hover:underline">
                legal@pelgo.com
              </a>
            </p>
          </section>
        </div>
      </div>

      <SiteFooter />
    </div>
  )
}
